import React, { useEffect, useState } from "react";
import { api, formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { toast } from "sonner";
import { RefreshCw, MapPin, ImageIcon, Truck, Fuel, Phone, MessageCircle, Map as MapIcon } from "lucide-react";
import GasCylinderIcon from "../../components/icons/GasCylinderIcon";
import DriverLocationDialog from "../../components/DriverLocationDialog";

// ---------------- Samsara-backed list (Autotanques or Cilindreras) ----------------

function SamsaraKindList({ kind, title, unitLabel, emptyHint, Icon }) {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [mapDriver, setMapDriver] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(`/drivers?kind=${kind}`);
      setList(data);
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [kind]); // eslint-disable-line

  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data } = await api.post("/drivers/sync-samsara", { kind });
      if (data.mocked) {
        toast.info(data.message || "Samsara no configurado");
      } else {
        toast.success(`Actualizadas ${data.synced} unidades`);
      }
      setLastRefresh(new Date());
      await load();
    } catch (e) {
      const detail = e?.response?.data?.detail || "No se pudo actualizar";
      toast.error(typeof detail === "string" ? detail : "Error al actualizar");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-company/10 text-[color:var(--company-primary)] flex items-center justify-center">
            <Icon size={20} />
          </div>
          <div>
            <p className="font-display text-xl font-extrabold leading-tight">{title}</p>
            <p className="text-xs text-neutral-500 mt-0.5">
              {list.length} {list.length === 1 ? "unidad" : "unidades"}
              {lastRefresh && ` · Actualizado ${lastRefresh.toLocaleTimeString("es")}`}
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          onClick={refresh}
          disabled={refreshing}
          className="h-11"
          data-testid={`refresh-${kind}-btn`}
        >
          <RefreshCw size={16} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Actualizando..." : "Actualizar"}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-500">Cargando…</p>
      ) : list.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-neutral-300 p-8 text-center">
          <Icon size={32} className="mx-auto text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-500 max-w-md mx-auto">{emptyHint}</p>
          <Button
            variant="outline"
            onClick={refresh}
            disabled={refreshing}
            className="h-10 mt-4"
          >
            <RefreshCw size={14} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
            Actualizar desde Samsara
          </Button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {list.map((d) => {
            const hasLoc = typeof d.current_lat === "number" && typeof d.current_lng === "number";
            return (
            <div
              key={d.driver_id}
              className="bg-white border border-neutral-200 rounded-xl p-4 flex items-start gap-3"
              data-testid={`driver-item-${d.driver_id}`}
            >
              <div className="h-12 w-12 rounded-full bg-company/10 text-[color:var(--company-primary)] border border-company/20 flex items-center justify-center overflow-hidden shrink-0">
                {d.image_url ? (
                  <img src={d.image_url} alt="" className="h-full w-full object-cover" />
                ) : (
                  <Icon size={18} />
                )}
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{d.name}</p>
                <p className="text-sm text-neutral-500 truncate">{d.phone || "—"} • {d.vehicle_plate || "—"}</p>
                <p className="text-xs text-neutral-400 mt-1 flex items-center gap-1">
                  <MapPin size={12} />
                  {hasLoc
                    ? `${d.current_lat.toFixed(4)}, ${d.current_lng.toFixed(4)}`
                    : "Sin ubicación"}
                </p>
                <div className="flex flex-wrap items-center gap-2 mt-2">
                  <span className={`text-xs font-semibold rounded-full px-2 py-0.5 ${
                    d.status === "available" ? "bg-green-50 text-green-700" :
                    d.status === "busy" ? "bg-amber-50 text-amber-700" : "bg-neutral-100 text-neutral-500"
                  }`}>
                    {d.status === "available" ? "Disponible" : d.status === "busy" ? "Ocupado" : "Fuera de línea"}
                  </span>
                  {d.samsara_vehicle_id && (
                    <span className="text-xs font-semibold rounded-full px-2 py-0.5 bg-blue-50 text-blue-700">Samsara</span>
                  )}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMapDriver(d)}
                disabled={!hasLoc}
                title={hasLoc ? "Ver ubicación en el mapa" : "Sin posición aún"}
                className={`h-9 w-9 rounded-full border flex items-center justify-center shrink-0 transition ${
                  hasLoc
                    ? "bg-company/10 text-[color:var(--company-primary)] border-company/30 hover:bg-company/20"
                    : "bg-neutral-50 text-neutral-300 border-neutral-200 cursor-not-allowed"
                }`}
                data-testid={`driver-map-btn-${d.driver_id}`}
              >
                <MapIcon size={16} />
              </button>
            </div>
          );})}
        </div>
      )}

      <DriverLocationDialog
        open={!!mapDriver}
        onOpenChange={(v) => { if (!v) setMapDriver(null); }}
        driver={mapDriver}
        unitLabel={unitLabel}
        Icon={Icon}
      />
    </div>
  );
}

// ---------------- Stations list (read-only here, managed in Empresa > Estaciones) ----------------

function StationsList() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [mapDriver, setMapDriver] = useState(null);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const { data } = await api.get("/stations");
      setList(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Error cargando estaciones");
    } finally {
      if (!silent) setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const refresh = async () => {
    setRefreshing(true);
    try {
      await load({ silent: true });
      toast.success("Listado actualizado");
    } finally {
      setRefreshing(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between gap-3 flex-wrap mb-4">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-company/10 text-[color:var(--company-primary)] flex items-center justify-center">
            <Fuel size={20} />
          </div>
          <div>
            <p className="font-display text-xl font-extrabold leading-tight">Estaciones</p>
            <p className="text-xs text-neutral-500 mt-0.5">
              {list.length} {list.length === 1 ? "estación" : "estaciones"} · Administra el listado en Empresa → Estaciones
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={refresh} disabled={refreshing} className="h-11" data-testid="refresh-estaciones-btn">
          <RefreshCw size={16} className={`mr-2 ${refreshing ? "animate-spin" : ""}`} />
          {refreshing ? "Actualizando..." : "Actualizar"}
        </Button>
      </div>

      {loading ? (
        <p className="text-sm text-neutral-500">Cargando…</p>
      ) : list.length === 0 ? (
        <div className="bg-white rounded-2xl border border-dashed border-neutral-300 p-8 text-center">
          <Fuel size={32} className="mx-auto text-neutral-400 mb-2" />
          <p className="text-sm text-neutral-500 max-w-md mx-auto">
            Aún no tienes estaciones. Agrégalas desde <b>Empresa → Estaciones</b>.
          </p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 gap-3">
          {list.map((s) => {
            const hasLoc = typeof s.lat === "number" && typeof s.lng === "number";
            return (
            <div
              key={s.station_id}
              className="bg-white border border-neutral-200 rounded-xl p-4 flex items-start gap-3"
              data-testid={`station-row-${s.station_id}`}
            >
              <div className="h-12 w-12 rounded-full bg-company/10 text-[color:var(--company-primary)] border border-company/20 flex items-center justify-center shrink-0">
                <Fuel size={18} />
              </div>
              <div className="min-w-0 flex-1">
                <p className="font-semibold truncate">{s.name}</p>
                {s.full_address && (
                  <p className="text-xs text-neutral-500 mt-0.5 flex items-start gap-1">
                    <MapPin size={11} className="mt-0.5 shrink-0" />
                    <span className="line-clamp-2">{s.full_address}</span>
                  </p>
                )}
                <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2 text-xs text-neutral-600">
                  {s.phone && <span className="inline-flex items-center gap-1"><Phone size={11} />{s.phone}</span>}
                  {s.whatsapp && <span className="inline-flex items-center gap-1 text-green-700"><MessageCircle size={11} />{s.whatsapp}</span>}
                </div>
              </div>
              <button
                type="button"
                onClick={() => setMapDriver({
                  name: s.name,
                  current_lat: s.lat,
                  current_lng: s.lng,
                  last_seen_at: null,
                })}
                disabled={!hasLoc}
                title={hasLoc ? "Ver ubicación en el mapa" : "Sin ubicación"}
                className={`h-9 w-9 rounded-full border flex items-center justify-center shrink-0 transition ${
                  hasLoc
                    ? "bg-company/10 text-[color:var(--company-primary)] border-company/30 hover:bg-company/20"
                    : "bg-neutral-50 text-neutral-300 border-neutral-200 cursor-not-allowed"
                }`}
                data-testid={`station-map-btn-${s.station_id}`}
              >
                <MapIcon size={16} />
              </button>
            </div>
          );})}
        </div>
      )}

      <DriverLocationDialog
        open={!!mapDriver}
        onOpenChange={(v) => { if (!v) setMapDriver(null); }}
        driver={mapDriver}
        unitLabel="Estación"
        Icon={Fuel}
      />
    </div>
  );
}

// ---------------- Main page with 3 tabs ----------------

export default function AdminDrivers() {
  return (
    <div className="min-h-screen bg-neutral-50">
      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        <div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Unidades de Reparto</h1>
          <p className="text-sm text-neutral-500 mt-1">
            Tus unidades agrupadas por canal. La configuración se define en <b>Empresa</b> (Autotanques, Cilindreras, Estaciones).
          </p>
        </div>

        <Tabs defaultValue="pipas" className="mt-6">
          <TabsList className="grid w-full grid-cols-3 h-12 bg-neutral-100 p-1 rounded-xl max-w-xl">
            <TabsTrigger value="pipas" className="data-[state=active]:bg-white rounded-lg gap-1.5" data-testid="tab-dr-pipas">
              <Truck size={16} className="shrink-0" />
              <span>Autotanques</span>
            </TabsTrigger>
            <TabsTrigger value="cilindreras" className="data-[state=active]:bg-white rounded-lg gap-1.5" data-testid="tab-dr-cilindreras">
              <GasCylinderIcon size={16} className="shrink-0" />
              <span>Cilindreras</span>
            </TabsTrigger>
            <TabsTrigger value="estaciones" className="data-[state=active]:bg-white rounded-lg gap-1.5" data-testid="tab-dr-estaciones">
              <Fuel size={16} className="shrink-0" />
              <span>Estaciones</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="pipas" className="mt-6">
            <SamsaraKindList
              kind="pipas"
              title="Autotanques"
              unitLabel="Autotanque"
              Icon={Truck}
              emptyHint="Aún no tienes autotanques sincronizados. Configura el token de Samsara y selecciona los autotanques en Empresa → Autotanques, luego pulsa Actualizar."
            />
          </TabsContent>

          <TabsContent value="cilindreras" className="mt-6">
            <SamsaraKindList
              kind="cilindreras"
              title="Cilindreras"
              unitLabel="Cilindrera"
              Icon={GasCylinderIcon}
              emptyHint="Aún no tienes cilindreras sincronizadas. Configura el token de Samsara y selecciona las cilindreras en Empresa → Cilindreras, luego pulsa Actualizar."
            />
          </TabsContent>

          <TabsContent value="estaciones" className="mt-6">
            <StationsList />
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
}
